import {
  Avatar,
  Box,
  Chip,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ScrollText } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../common/components/PageHeader";
import { toast } from "../../../common/components/Toast";
import { useAuditLogs } from "../../api/admin";
import { StatCard } from "../../shared/components/StatCard";

function AuditLogs() {
  const { t } = useTranslation("admin_panel");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const { logs, totalCount, totalPages, error } = useAuditLogs(
    currentPage,
    pageSize,
  );

  useEffect(() => {
    if (error) {
      toast.error("Failed to load audit logs");
      console.error("Error fetching audit logs:", error);
    }
  }, [error]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      login_as_user: "Login As User",
    };
    return labels[action] || action;
  };

  const getActionColor = (action) => {
    const colors = {
      login_as_user: "warning",
    };
    return colors[action] || "default";
  };

  return (
    <>
      <PageHeader
        title={t("auditLogs.title")}
        description={t("auditLogs.description")}
        appendActions={
          <Box sx={{ minWidth: 230 }}>
            <StatCard
              title={t("auditLogs.totalLogs")}
              value={totalCount}
              icon={ScrollText}
              color="#9c27b0"
            />
          </Box>
        }
      />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Admin</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Target User</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length > 0 ? (
              logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        src={log.admin_user?.avatar}
                        sx={{ width: 32, height: 32, fontSize: 14 }}
                      >
                        {log.admin_user?.name?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="bodySm" sx={{ fontWeight: 500 }}>
                          {log.admin_user?.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.admin_user?.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getActionLabel(log.action)}
                      color={getActionColor(log.action)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {log.target_user ? (
                      <Box>
                        <Typography variant="bodySm">
                          {log.target_user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.target_user.email}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="bodySm" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="bodySm"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {log.ip_address || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {formatDate(log.created_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No audit logs found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </>
  );
}

export default AuditLogs;
